/**
 * TenantMenuSection
 *
 * Renders the tenant block inside the user / avatar menu.
 *
 * Shape (Pattern B + "Azure Portal switch directory" flow):
 *  - Top: current tenant displayName as a static informational row — it
 *    tells you which tenant you're in but isn't itself a control.
 *  - Bottom: a single "Switch tenant…" menu item that fires
 *    `onSwitchTenantClick` so the parent (RootLayout) can render the
 *    TenantSwitchDialog above the menu. Mounting the dialog *inside*
 *    the menu doesn't work — Fluent auto-closes the menu when the item
 *    is clicked, which unmounts the popover (and any dialog inside it)
 *    before React can paint.
 *
 * Previously this section listed every tenant inline as MenuItemRadios.
 * That didn't scale past ~10 entries — users with dozens of tenants got
 * a runaway-tall menu. The dialog handles long lists gracefully (search,
 * scroll, keyboard nav) without bloating the menu.
 *
 * Returns null when the user has 0 or 1 tenant — nothing to switch.
 */

import { useEffect, useRef } from 'react';
import {
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowSwapRegular } from '@fluentui/react-icons';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { useTenants } from '@/hooks/useAzureResources';
import { useTelemetry } from '@/hooks/useTelemetry';
import { getTenantIdFromUrl } from '@/auth/msalConfig';

interface TenantMenuSectionProps {
  /**
   * Invoked when the user clicks "Switch tenant…". The parent owns the
   * dialog state + mount so the dialog isn't torn down when Fluent
   * auto-closes the avatar menu on click.
   */
  onSwitchTenantClick: () => void;
}

const useStyles = makeStyles({
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  currentRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    paddingBottom: tokens.spacingVerticalS,
  },
  currentName: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '240px',
  },
  currentDomain: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '240px',
  },
  switchHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export const TenantMenuSection = ({
  onSwitchTenantClick,
}: TenantMenuSectionProps) => {
  const styles = useStyles();
  const urlTenantId = useRef(getTenantIdFromUrl());

  const { selectedTenantId, setSelectedTenant } = useAppStore();
  const { account } = useAuth();
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const { trackEvent } = useTelemetry();

  // Auto-select tenant on first load: URL tenant > home tenant > first
  // tenant. Preserves the chip-era behavior so the right tenant is
  // active before the user opens the menu for the first time.
  useEffect(() => {
    if (selectedTenantId || !tenants?.length) return;

    const fromUrl = urlTenantId.current;
    if (fromUrl) {
      const urlTenant = tenants.find((t) => t.tenantId === fromUrl);
      if (urlTenant) {
        setSelectedTenant(urlTenant.tenantId);
        return;
      }
    }
    const homeTenant = account?.tenantId
      ? tenants.find((t) => t.tenantId === account.tenantId)
      : undefined;
    const defaultTenant = homeTenant ?? tenants[0];
    if (defaultTenant) {
      setSelectedTenant(defaultTenant.tenantId);
    }
  }, [tenants, selectedTenantId, setSelectedTenant, account?.tenantId]);

  if (tenantsLoading) {
    return (
      <>
        <MenuDivider />
        <div
          className={styles.loadingRow}
          role="status"
          aria-live="polite"
          aria-label="Loading tenants"
        >
          <Spinner size="tiny" />
          <span>Loading tenants…</span>
        </div>
      </>
    );
  }

  // Single tenant — nothing to switch between, hide the section entirely.
  if (!tenants || tenants.length < 2) return null;

  const currentTenant = tenants.find((t) => t.tenantId === selectedTenantId);
  const otherTenantCount = tenants.length - 1;

  return (
    <>
      <MenuDivider />
      <MenuGroup>
        <MenuGroupHeader>Tenant</MenuGroupHeader>
        {currentTenant ? (
          <div className={styles.currentRow}>
            <span className={styles.currentName} title={currentTenant.displayName}>
              {currentTenant.displayName}
            </span>
            <span
              className={styles.currentDomain}
              title={currentTenant.defaultDomain}
            >
              {currentTenant.defaultDomain}
            </span>
          </div>
        ) : null}
        <MenuItem
          icon={<ArrowSwapRegular />}
          onClick={() => {
            trackEvent('tenant.switch_dialog.requested');
            onSwitchTenantClick();
          }}
          // The Fluent MenuItem `secondaryContent` slot renders on the
          // right and is visible-but-not-the-label, so screen readers
          // announce the menu item by its primary label ("Switch
          // tenant"). Showing the count gives sighted users a hint at
          // the scale of the list before they open the dialog.
          secondaryContent={
            <Text className={styles.switchHint}>
              {otherTenantCount} {otherTenantCount === 1 ? 'other' : 'others'}
            </Text>
          }
        >
          Switch tenant…
        </MenuItem>
      </MenuGroup>
    </>
  );
};
